import { isMicroAppMode, helper } from '@/microAdapt/utils'
import { Message } from 'element-ui'
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
dayjs.extend(duration);
import logServeCof from '@/plugins/logServeCof.js';
import axios from 'axios'

/**
 * 根据节点ID找出指定节点
 * @param {Array} data 树状结构数据
 * @param {String} id 节点ID
 * @returns 返回指定节点
 */
export function findNodeById(data, id) {
    for (const node of data) {
        if (node.nodeId === id) return node;
        if (node.subNodes && node.subNodes.length) {
            const res = this.findNodeById(node.subNodes, id);
            if (res) return res;
        }
    }
    return null;
}

// 复制文本
export const copyText = async (text) => {
    if (!text) return;
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            Message.success('复制成功');
            return;
        }
    } catch (error) {
        console.warn('Clipboard API 失败:', error);
    }

    try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed'; // 避免滚动
        textarea.style.opacity = '0';     // 透明隐藏
        document.body.appendChild(textarea);
        textarea.select();

        const success = document.execCommand('copy');
        document.body.removeChild(textarea);

        if (success) {
            Message.success('复制成功');
        } else {
            throw new Error('execCommand 复制失败');
        }
    } catch (error) {
        console.error('降级复制失败:', error);
        Message.error('复制失败，请手动复制');
    }
}

/**
 * 找出指定节点的所有父节点(包括节点本身)
 * @param {Node} node 指定节点
 * @param {Array} array 存放父节点的数组
 */
export function getParentNode(node, array) {
    if (node.parent) {
        array.push(node);
        this.getParentNode(node.parent, array);
    }
}

/**
 * 格式化时间
 * @param {String} utcTime UTC时间字符串
 * @returns 格式化后的时间字符串
 */
export function formatTime(utcTime) {
    if (!utcTime) return;
    let date = new Date(Date.parse(utcTime));
    let year = date.getFullYear();
    let month = pad2(date.getMonth() + 1);
    let day = pad2(date.getDate());
    let hour = pad2(date.getHours());
    let minute = pad2(date.getMinutes());
    let second = pad2(date.getSeconds());
    return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
}

export function pad2(n) {
    return n < 10 ? "0" + n : n;
}

/**
 * 
 * @param {Number} length 文件大小
 * @returns 格式化后的文件大小字符串
 */
export function formatFileSize(bytes, decimalPlaces = 2, locale = 'en') {
    if (!bytes || bytes === 0) return { fileSize: '--', unit: '' };
    // 处理极小值（小于1字节的情况）
    if (bytes < 1) {
        return {
            fileSize: 0,
            unit: 'B' // 强制使用最小单位（B或字节）
        };
    }
    const units = {
        en: ['B', 'KB', 'MB', 'GB', 'TB'],
        zh: ['字节', 'KB', 'MB', 'GB', 'TB']
    };
    const unitSet = units[locale] || units.en;

    const unitIndex = Math.min(
        Math.floor(Math.log(bytes) / Math.log(1024)),
        unitSet.length - 1
    );
    const size = preciseDivision(bytes, Math.pow(1024, unitIndex));

    const formattedSize = formatWithoutRounding(size, decimalPlaces);
    return {
        fileSize: Number(formattedSize),
        unit: unitSet[unitIndex]
    };
}

// 高精度除法函数
function preciseDivision(a, b) {
    const factor = Math.pow(10, 15); // 放大因子
    return (a * factor) / (b * factor);
}

// 不四舍五入的格式化
function formatWithoutRounding(num, decimalPlaces) {
    const factor = Math.pow(10, decimalPlaces);
    const truncated = Math.floor(num * factor) / factor;
    return truncated.toFixed(decimalPlaces);
}

export class Converter {
    constructor(value) {
        this.value = value;
        this.unit = null;
    }

    from(unit) {
        const validUnits = ['B', 'KB', 'MB', 'GB', 'TB'];
        if (!validUnits.includes(unit)) {
            throw new Error(`无效单位，支持: ${validUnits.join(', ')}`);
        }
        this.unit = unit;
        return this;
    }

    toB() {
        const factors = {
            B: 1,
            KB: 1024,
            MB: 1024 * 1024,
            GB: 1024 * 1024 * 1024,
            TB: 1024 * 1024 * 1024 * 1024
        };
        return this.value * factors[this.unit];
    }
    toKB() {
        return this.toB() / 1024;
    }
    toMB() {
        return this.toB() / (1024 * 1024);
    }
    toGB() {
        return this.toB() / (1024 * 1024 * 1024);
    }

}

export function formatSecond(second) {
    let timeString = ""
    let d = parseInt(second / 60 / 60 / 24);
    if (d) {
        timeString += `${d}天`
    }
    let h = parseInt(second / 60 / 60 % 24);
    if (h) {
        h = h < 10 ? '0' + h : h;
        timeString += `${h}小时`
    }
    let m = parseInt(second / 60 % 60);
    if (m) {
        m = m < 10 ? '0' + m : m;
        timeString += `${m}分钟`
    }
    let s = parseInt(second % 60);
    if (s) {
        s = s < 10 ? '0' + s : s;
        timeString += `${s}秒`
    }
    if (!timeString) {
        timeString = "--"
    }
    return timeString;
}

/**
 * 
 * @param {*} time 
 * @param {*} unit Y|年 M|月 D|日 H|时 m|分 s|秒
 * @description 格式化时间为xx天xx小时xx分钟xx秒
 */
export const formatTimeToText = (time, unit = 's') => {
    if (!time && time !== 0) {
        return '--'
    }
    if (time === 0) {
        return '0天'
    }
    let milliseconds;
    switch (unit) {
        case 'Y': // 年 → 按 365.2425 天计算（考虑闰年）
            milliseconds = time * 365.2425 * 86400 * 1000;
            break;
        case 'M': // 月 → 按 30.436875 天计算（平均值）
            milliseconds = time * 30.436875 * 86400 * 1000;
            break;
        case 'D': // 天
            milliseconds = time * 86400 * 1000;
            break;
        case 'H': // 小时
            milliseconds = time * 3600 * 1000;
            break;
        case 'm': // 分钟
            milliseconds = time * 60 * 1000;
            break;
        case 's': // 秒
            milliseconds = time * 1000;
            break;
        case 'ms': // 毫秒
            milliseconds = time;
            break;
        default: // 默认按毫秒处理
            milliseconds = time;
    }
    const durationObj = dayjs.duration(milliseconds);
    const days = Math.floor(durationObj.asDays());
    const hours = durationObj.hours();
    const minutes = durationObj.minutes();
    const seconds = durationObj.seconds();
    return `${days}天${hours}小时${minutes}分${seconds}秒`;
}

/**
 * 格式化进度
 * @param {Number} progress 
 * @returns 格式化后的字符串
 */
export function formatText(progress) {
    let string = "";
    if (formatRatio(progress) !== 100) {
        string = `${formatRatio(progress)}%`;
    }
    return string;
}

export function formatRatio(progress) {
    return progress / 100;
}

/**
 * 返回token
 * @returns token
 */
export function getToken() {
    if (isMicroAppMode() && typeof helper.getToken === 'function') {
        return helper.getToken()
    } else {
        return sessionStorage.getItem("token");
    }
}


/**
 * 返回tokenId
 * @returns tokenId
 */
export function getTokenId() {
    return sessionStorage.getItem("tokenId");
}

/**
 * 压缩图片到指定大小
 * @param baseImg base64图片
 * @param maxSize 单位kb
 */
export function compressImgBySize(baseImg, maxSize = 200) {
    return new Promise((resolve) => {
        const strLength = baseImg.length
        const fileLength = parseInt(strLength - (strLength / 8) * 2)
        let size = parseInt((fileLength / 1024).toFixed(2))

        if (size <= maxSize) {
            let blobFile = dataURLtoFile(baseImg, 'image/jpeg');
            resolve(blobFile)
            return
        }

        const img = new Image()
        // img.src = 'data:image/jpeg;base64,' + baseImg
        img.src = baseImg
        img.onload = () => {
            const canvas = imgToCanvas(img)
            let resUrl = ''
            let quality = 0.9
            while (size > maxSize && quality > 0) {
                resUrl = canvas.toDataURL('image/jpeg', quality);
                const resLength = resUrl.length
                const resFileLength = parseInt(resLength - (resLength / 8) * 2)
                size = parseInt((resFileLength / 1024).toFixed(2))
                quality = Number((quality - 0.1).toFixed(1));
            }
            let blobFile = dataURLtoFile(resUrl, 'image/jpeg');
            resolve(blobFile);
        }
        img.onerror = () => {
            resolve(baseImg)
        }
    })
}

export function getBase64(file) {
    return new Promise((resolve, reject) => {
        let reader = new FileReader(); //定义方法读取文件
        reader.readAsDataURL(file); //开始读文件  本身是图片的二进制数据 进行base64加密形成字符串
        reader.onload = () => resolve(reader.result); //成功返回对应的值 reader.result可以直接放在img标签中使用
        reader.onerror = (error) => reject(error); //失败返回失败信息
    });
}

// 把image 转换为 canvas对象
export function imgToCanvas(image) {
    var canvas = document.createElement('canvas')
    canvas.width = image.width
    canvas.height = image.height
    canvas.getContext('2d').drawImage(image, 0, 0)
    return canvas
}

// 将base64转换为blob
function dataURLtoFile(dataURI, type) {
    let binary = atob(dataURI.split(',')[1]);
    let array = [];
    for (let i = 0; i < binary.length; i++) {
        array.push(binary.charCodeAt(i));
    }
    return new Blob([new Uint8Array(array)], { type: type });
}


/**
 * 根据文件状态返回指定颜色及图标
 * @param {Number} status 
 * @returns 
 */
export function getIconAndColor(status) {
    let icon = "";
    let color = "";
    switch (status) {
        case 0:
            icon = "el-icon-time";
            color = "#696969";
            break;
        case 1:
            icon = "el-icon-video-pause";
            color = "#0bdddb";
            break;
        case 2:
            icon = "el-icon-video-play";
            color = "#ee910b";
            break;
        case 3:
            icon = "el-icon-video-pause";
            color = "#ee910b";
            break;
        case 4:
            icon = "el-icon-circle-check";
            color = "#01ff31";
            break;
        case 5:
            icon = "el-icon-refresh";
            color = "#01ff31";
            break;
        case 6:
            icon = "el-icon-time";
            color = "#696969 ";
            break;
        case 7:
            icon = "el-icon-refresh";
            color = "#01ff31";
            break;
        case 8:
            icon = "el-icon-circle-close";
            color = "#ff0A27 ";
            break;
    }
    return { icon, color };
}

/**
 * 关闭浏览器标签页(兼容浏览器)
 */
export function exitSystem() {
    if (navigator.userAgent.indexOf("MSIE") > 0) {
        if (navigator.userAgent.indexOf("MSIE 6.0") > 0) {
            //适用于ie6不带提示关闭窗口
            window.opener = null;
            window.close();
        } else {
            window.open("", "_top"); //ie7之后不提示关闭窗口
            window.top.close();
        }
    } else if (navigator.userAgent.indexOf("Firefox") > 0) {
        //Firefox不提示关闭窗口
        window.location.href = "about:blank ";
    } else {
        window.opener = null;
        window.open("", "_self", ""); //其他浏览器
        window.close();
    }
}

export function encryptPassword(password) {
    if (!window.isEncryptPassword) return password;
    if (window.isEncryptPassword) {
        return btoa(password);
    }
}

/**
 * 生成Guid
 * @returns 
 */

export function guid() {
    function S4() {
        return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
    }
    return (S4() + S4() + "-" + S4() + "-" + S4() + "-" + S4() + "-" + S4() + S4() + S4());
}

export function isMobileDevice() {
    const userAgent = navigator.userAgent.toLowerCase();
    const patterns = [
        /ipad/i,
        /iphone os/i,
        /midp/i,
        /rv:1.2.3.4/i,
        /ucweb/i,
        /android/i,
        /windows ce/i,
        /windows mobil/i
    ];

    return patterns.some(pattern => pattern.test(userAgent));
}

export async function isTemplateVersionConsistentWithEngine() {
    const url = `${window.serverUrl}/${logServeCof.logServeCof()}/basic/health/template`
    try {
        const response = await axios.get(url);
        const templateVersion = response.data;
        const sdkVersion = window.BlackHole3D.getVersion();

        const lastTemplateVersionNumber = templateVersion.split('.').pop();
        const lastSdkVersionNumber = sdkVersion.split('.').pop();
        const rule1 = (lastTemplateVersionNumber, lastSdkVersionNumber) => {
            if (lastTemplateVersionNumber >= 3282) {
                return lastSdkVersionNumber > 3247;
            } else {
                return true
            }
        };

        return rule1(lastTemplateVersionNumber, lastSdkVersionNumber);
    } catch (error) {
        if (error.response?.status === 404) {
            return true
        } else {
            return false
        }
    }


}

